<template>
    <div>
        <div class="text-sm">
            <p class="modal-heading mb-4">New Recipient</p>
            
            <div class="form-group">
                <label>Type</label>
                <select v-model="form.type" class="form-control">
                    <option value="1">Mobile phone number</option>
                    <option value="0">Email</option>
                    <option value="2">Practitioner</option>
                </select>
            </div>

            <div class="form-group">
                <label>Name</label>
                <input v-model="form.name" type="text" class="form-control" /> 
            </div>

            <div v-if="form.type != 2" class="form-group">
                <div class="alert alert-warning" role="alert">
                    Please refrain from using personal contact details
                </div>
            </div>
            
            <template v-if="form.type != 2">
                <div class="form-group">
                    <div class="row align-items-end">
                        <div class="col-md-8">
                            <label v-if="form.type == 1">Mobile number</label>
                            <label v-if="form.type == 0">Email address</label>
                            <input v-model="form.value" type="text" class="form-control" />
                        </div>
                        <div class="col-md-4">
                            <button @click="verify()" :disabled="loading || verificationLoading || !canVerify" class="btn btn-primary btn-sm d-flex flex-1" :class="{disabled : loading || verificationLoading || !canVerify}">Verify <span v-if="verificationLoading" class="btn-loading"></span></button>
                        </div>
                    </div>
                    <div v-if="verificationErrorMessage" class="text-danger text-sm">{{ verificationErrorMessage }}</div>
                    <div v-if="verificationError" class="text-danger text-sm">Sorry, something went wrong while verifying</div>
                </div>

                <div class="form-group">
                    <div class="row">
                        <div class="col-md-8">
                            <label>Verification code</label>
                            <input v-model="form.verificationCode" type="text" class="form-control" />
                        </div>
                    </div>
                    <div v-if="verificationMessageSent" class="text-success text-sm">A verification code has been sent to you</div>
                    <div v-if="verificationSaveFailure" class="text-danger text-sm">Verification code invalid</div>
                </div>
            </template>
            
            <div v-if="form.type == 2" class="form-group">
                <div class="form-group">
                    <label>Select a practitioner</label>
                    <select v-model="form.value" class="form-control">
                        <option selected disabled value=null>Please select a practitioner</option>
                        <option v-for="p in practitioners" :value="p.id" :key="p.id">{{ p.name }}</option>
                    </select>
                    <div v-if="practitionersError" class="text-danger text-sm">Sorry, something went wrong while fetching practitioners</div>
                </div>
            </div>
        </div>

        <p class="text-xs mb-0">The intended use of this system is as an alternative to in-clinic testing by enabling the oral glucose tolerance test (OGTT) to be performed at home as a screening tool for planned care pathways. It is not an emergency service or a substitute for urgent medical care. Healthcare professionals should not solely rely on notifications to intervene in patient care. While alerts are triggered by glucose levels exceeding set thresholds, clinical judgment and comprehensive assessment remain essential for decision-making. Please note that notifications may not always arrive promptly and are provided for informational purposes only. Use this system cautiously as a supplementary tool. By enabling this feature you acknowledge the aforementioned constraints and to use this feature only for information purposes. Any implementation deviating from the intended use should be risk assessed and captured in the healthcare organisation's own DCB0160 documentation.</p>

        <div class="d-flex align-items-center mt-4">
            <button @click="close()" :disabled="loading || verificationLoading" class="btn btn-cancel btn-lg lg-shadow d-flex flex-grow-1 justify-content-center align-items-center" :class="{disabled : loading || verificationLoading}">Cancel</button>
            <button @click="save()" :disabled="loading || verificationLoading || !canSave" class="btn btn-success btn-lg lg-shadow d-flex flex-grow-1 ml-3 justify-content-center align-items-center" :class="{disabled : loading || verificationLoading || !canSave}">Add <span v-if="loading" class="btn-loading"></span></button>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import {mapGetters, mapActions} from 'vuex'
export default {
    data() {
        return {
            loading: false,
            verificationLoading: false,
            form: {
                name: null,
                type: 1,
                value: null,
                verificationCode: null
            },
            verificationCode: null,
            error: false,
            verificationMessageSent: false,
            verificationError: false,
            verificationSaveFailure: false,
            verificationErrorMessage: null,
            practitionersError: false
        }
    },

    async mounted() {
        if(!this.practitioners.length) {
            try {
                await this.getPractitioners()
            } catch {
                this.practitionersError = true
            }
        }
    },

    computed: {
        ...mapGetters({
            domainId: 'OrganisationStore/GetDomainId',
            organisationId: 'UserStore/GetUserOrganisationId',
            isNotificationManager: 'UserStore/GetUserIsNotificationManager',
            practitioners: 'PractitionerStore/GetAllPractitioners'
        }),

        canSave() {
            if(this.form.type == null || this.form.type == '') return false 

            if(this.form.type != 2) {
                return this.form.name != null &&
                       this.form.value != null && 
                       this.form.verificationCode != null &&
                       this.form.name != '' &&
                       this.form.value != '' && 
                       this.form.verificationCode != ''
            }

            return this.form.name != null &&
                   this.form.name != '' &&
                   this.form.value != null &&
                   this.form.value != ''
        },

        canVerify() {
            if(this.form.type == 1) {
                    return this.form.value != null &&
                            this.form.value != '' && 
                            this.form.value.match(/^\+[1-9]{1}[0-9]{3,14}$/)
            }
            if(this.form.type == 0) {
                    return this.form.value != null &&
                            this.form.value != ''
            }

            return false
        }
    },

    methods: {
        ...mapActions({
            setShouldRefreshNotifications: 'AppStore/SetShouldRefreshNotifications',
            getPractitioners: 'PractitionerStore/GetAllPractitioners',
            setNotification: 'NotificationStore/SetNotification'
        }),

        async verify() {
            if(this.form.type == null || this.form.value == null) return 

            var data = {
                type: this.form.type,
                phoneNumberOrEmailAddress: this.form.value
            }

            this.verificationError = false // something went wrong when calling the endpoint
            this.verificationMessageSent = false // the call succeeded
            this.verificationLoading = true // show the spinner until the request returns
            this.verificationErrorMessage = null // the error message from the response
            this.verificationSaveFailure = false // something went wrong when adding / saving

            try {
                let response = await axios.post(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/recipients/verify`, data)
                this.verificationCode = response.data.verificationCode
                this.verificationLoading = false
                if(response.data.isError) {
                    this.form.value = null
                    this.verificationErrorMessage = response.data.errorMessage
                } else {
                    this.verificationMessageSent = true
                }
            } catch {
                this.verificationError = true
                this.verificationLoading = false
            } finally {
                this.verificationLoading = false
            }
        },

        async save() {
            this.verificationSaveFailure = false
            this.verificationMessageSent = false

            if(this.form.verificationCode !== this.verificationCode) {
                this.verificationMessageSent = false
                this.verificationSaveFailure = true
                return
            }

            var data = {
                name: this.form.name,
                isActive: true,
                domainId: this.domainId,
                organisationId: this.organisationId,
                type: this.form.type,
                value: this.form.value
            }

            this.error = false
            this.loading = true 

            try {
                await axios.post(`${process.env.VUE_APP_AXIOS_ORGANISATION_BASE_URL}/api/v1/notifications/recipients`, data)
                this.setShouldRefreshNotifications(true)
                this.$emit('close')
                this.setNotification({
                    type: 'success',
                    title: 'Success!',
                    message: 'The recipient was saved successfully.'
                })
            } catch {
                this.error = true
                this.loading = false
                this.setNotification({
                    type: 'error',
                    title: 'Error!',
                    message: 'Sorry, something went wrong. Please try again.'
                })
            } finally {
                this.loading = false
            }
        },

        close() {
            this.$emit('close')
        }
    }
}
</script>